import React from 'react';
import { Redirect } from 'react-router-dom';
import { Button, Container, Dimmer, Form, Icon, Label, Loader } from 'semantic-ui-react';
import MyForm from '../../admin/components/forms/form';
import MyFormState from '../../admin/components/forms/MyFormState';
import S3Manager from '../../admin/components/s3/s3manager';
import ImageOnS3 from '../../components/imageOnS3/imageOnS3.component';
import { ProductModel } from '../../entities/admin.product.model';
import { ImageModel } from '../../entities/image.model';
import { createBarcode, generateUniqueCode } from '../../utils/BardCodeUtils';
import ImageCropperModal from '../../components/image-cropper/imageCropper.modal';
import { buildProductGalleryWebpVariants, buildProductMainWebpVariants } from '../../utils/imageVariants';
import './product.form.scss';

interface FormProps {
    match: any,
    history: any
}

interface FormState extends MyFormState {
    parentCategories: any[],
    subCategories: any[],
    // categoryTypes: string[],
    // categoryUsages: string[],
    brands: any[],
    providers: any[],
    units: any[],
    model: ProductModel,
    barcodeImage: string,
    cropOpen: boolean,
    cropImageSrc: string,
    cropKeyPrefix: string,
    cropForSlot: 'main' | 1 | 2 | 3 | null,
    cropSourceFileSize: number | null,
}

class ProviderProductForm extends MyForm<FormProps, FormState> {
    entity = "product";
    typeArr: string[] = [];
    allSubCategories: any[] = [];
    allCategoryMultiOptions: any[] = [];
    triedUsages = 0;
    isProvider = true;

    private trackUnsavedUploads = (...keys: Array<string | undefined>) => {
        keys.forEach((key) => {
            if (!key) return;
            if (!this.imagesUploaded.includes(key)) {
                this.imagesUploaded.push(key);
            }
        });
    }

    async deleteOldImagesAndNavigateToList() {
        this.imagesUploaded = [];
        // Update originalModel with the saved data so print button becomes enabled
        this.originalModel = Object.assign({}, this.state.model);
        await super.deleteOldImagesAndNavigateToList();
    }

    constructor(props: any) {
        super(props);

        let units: any[] = [];
        ['грамм', 'кг', 'мл', 'литр', 'ширхэг'].forEach((unit: string) => {
            units.push({ key: unit, value: unit, text: unit })
        })

        this.state = {
            model: new ProductModel,
            successMsg: "",
            errorMsg: "",
            loading: true,
            editing: false,
            editingSlug: false,
            slugValid: true,
            imageObj: ImageModel.empty(),
            imageObjs: {
                lg: ImageModel.empty(), sm: ImageModel.empty(), xs: ImageModel.empty(),
                image1: ImageModel.empty(), image2: ImageModel.empty(), image3: ImageModel.empty(),
                image4: ImageModel.empty(), image5: ImageModel.empty(), image6: ImageModel.empty()
            },
            parentCategories: [],
            subCategories: [],
            providers: [],
            brands: [],
            // categoryTypes: [],
            units: units,
            redirect: "",
            barcodeImage: "",
            cropOpen: false,
            cropImageSrc: '',
            cropKeyPrefix: '',
            cropForSlot: null,
            cropSourceFileSize: null,
        }

        this.onParentCategoryChange = this.onParentCategoryChange.bind(this);
        this.onSubCategoryChange = this.onSubCategoryChange.bind(this);
        this.onBrandChange = this.onBrandChange.bind(this);
        this.onSizeUnitChange = this.onSizeUnitChange.bind(this);
        this.onSaleActiveChange = this.onSaleActiveChange.bind(this);
        this.onSalePriceChange = this.onSalePriceChange.bind(this);
        this.onSaleUntilChange = this.onSaleUntilChange.bind(this);
        this.onDeliveryDayChange = this.onDeliveryDayChange.bind(this);
        this.onUnitAdded = this.onUnitAdded.bind(this);
        this.onBarcodeChange = this.onBarcodeChange.bind(this);
        this.generateBarcode = this.generateBarcode.bind(this);
        this.printBarcodes = this.printBarcodes.bind(this);
    }

    onMainImageSelected = (event: any) => {
        const file = event?.target?.files?.[0];
        if (!file) return;
        const src = URL.createObjectURL(file);
        const iso = new Date().toISOString();
        const prefix = `${this.entity}/${this.imageNamePrefix || 'product'}-main-${iso}`;
        if (event?.target) event.target.value = '';
        this.setState({ cropOpen: true, cropImageSrc: src, cropKeyPrefix: prefix, cropForSlot: 'main', cropSourceFileSize: file.size });
    }

    onProductImageSelected = (slot: 1 | 2 | 3) => (event: any) => {
        const file = event?.target?.files?.[0];
        if (!file) return;
        const src = URL.createObjectURL(file);
        const iso = new Date().toISOString();
        const prefix = `${this.entity}/${this.imageNamePrefix || 'product'}-img${slot}-${iso}`;
        if (event?.target) event.target.value = '';
        this.setState({ cropOpen: true, cropImageSrc: src, cropKeyPrefix: prefix, cropForSlot: slot, cropSourceFileSize: file.size });
    }

    onMainImageCropped = async (blob: Blob) => {
        const { cropKeyPrefix } = this.state;
        if (!cropKeyPrefix) return;

        if (this.state.imageObjs?.lg?.uploaded && this.state.imageObjs.lg.name) this.imagesToDelete.push(this.state.imageObjs.lg.name);
        if (this.state.imageObjs?.sm?.uploaded && this.state.imageObjs.sm.name) this.imagesToDelete.push(this.state.imageObjs.sm.name);
        if (this.state.imageObjs?.xs?.uploaded && this.state.imageObjs.xs.name) this.imagesToDelete.push(this.state.imageObjs.xs.name);

        this.setState({ loading: true });
        try {
            const { lg, sm, xs } = await buildProductMainWebpVariants(blob);
            const keyLg = `${cropKeyPrefix}-lg.webp`;
            const keySm = `${cropKeyPrefix}-sm.webp`;
            const keyXs = `${cropKeyPrefix}-xs.webp`;
            await S3Manager.uploadImage(keyLg, lg, true, 'image/webp');
            await S3Manager.uploadImage(keySm, sm, true, 'image/webp');
            await S3Manager.uploadImage(keyXs, xs, true, 'image/webp');
            this.trackUnsavedUploads(keyLg, keySm, keyXs);
            this.recordSaved = false;

            const model: any = this.state.model;
            model.imageLgUrl = keyLg;
            model.imageSmUrl = keySm;
            model.imageXsUrl = keyXs;

            const imageObjs = this.state.imageObjs;
            imageObjs.lg = new ImageModel(undefined, keyLg, true, true);
            imageObjs.sm = new ImageModel(undefined, keySm, true, true);
            imageObjs.xs = new ImageModel(undefined, keyXs, true, true);

            this.setState({
                model,
                imageObjs,
                cropOpen: false,
                cropImageSrc: '',
                cropKeyPrefix: '',
                cropForSlot: null,
                cropSourceFileSize: null,
            });
        } catch (err) {
            console.error(err);
            this.setState({ errorMsg: 'Зураг боловсруулахад алдаа гарлаа' });
        } finally {
            this.setState({ loading: false });
        }
    }

    onProductImageCropped = async (slot: 1 | 2 | 3, blob: Blob) => {
        const { cropKeyPrefix } = this.state;
        if (!cropKeyPrefix) return;
        const bigKey = slot === 1 ? 'image1' : slot === 2 ? 'image2' : 'image3';
        const smKey = slot === 1 ? 'image4' : slot === 2 ? 'image5' : 'image6';
        if (this.state.imageObjs[bigKey]?.uploaded && this.state.imageObjs[bigKey].name) this.imagesToDelete.push(this.state.imageObjs[bigKey].name);
        if (this.state.imageObjs[smKey]?.uploaded && this.state.imageObjs[smKey].name) this.imagesToDelete.push(this.state.imageObjs[smKey].name);
        this.setState({ loading: true });
        try {
            const { lg, sm } = await buildProductGalleryWebpVariants(blob);
            const keyLg = `${cropKeyPrefix}-lg.webp`;
            const keySm = `${cropKeyPrefix}-sm.webp`;
            await S3Manager.uploadImage(keyLg, lg, true, 'image/webp');
            await S3Manager.uploadImage(keySm, sm, true, 'image/webp');
            this.trackUnsavedUploads(keyLg, keySm);
            this.recordSaved = false;
            const model: any = this.state.model;
            const imageObjs = this.state.imageObjs;
            if (slot === 1) {
                model.image1 = keyLg;
                model.image4 = keySm;
                imageObjs.image1 = new ImageModel(undefined, keyLg, true, true);
                imageObjs.image4 = new ImageModel(undefined, keySm, true, true);
            } else if (slot === 2) {
                model.image2 = keyLg;
                model.image5 = keySm;
                imageObjs.image2 = new ImageModel(undefined, keyLg, true, true);
                imageObjs.image5 = new ImageModel(undefined, keySm, true, true);
            } else {
                model.image3 = keyLg;
                model.image6 = keySm;
                imageObjs.image3 = new ImageModel(undefined, keyLg, true, true);
                imageObjs.image6 = new ImageModel(undefined, keySm, true, true);
            }
            this.setState({ model, imageObjs, cropOpen: false, cropImageSrc: '', cropKeyPrefix: '', cropForSlot: null, cropSourceFileSize: null });
        } catch (err) {
            console.error(err);
            this.setState({ errorMsg: 'Зураг боловсруулахад алдаа гарлаа' });
        } finally {
            this.setState({ loading: false });
        }
    }

    handleCropDone = async (blob: Blob) => {
        const { cropForSlot } = this.state;
        if (cropForSlot === 'main' || cropForSlot === null) {
            await this.onMainImageCropped(blob);
        } else if (cropForSlot === 1 || cropForSlot === 2 || cropForSlot === 3) {
            await this.onProductImageCropped(cropForSlot, blob);
        }
    }

    deleteProductImage = (slot: 1 | 2 | 3) => {
        const bigKey = slot === 1 ? 'image1' : slot === 2 ? 'image2' : 'image3';
        const smKey = slot === 1 ? 'image4' : slot === 2 ? 'image5' : 'image6';
        const model: any = this.state.model;
        const imageObjs = this.state.imageObjs;
        if (model[bigKey]) this.imagesToDelete.push(model[bigKey]);
        if (model[smKey]) this.imagesToDelete.push(model[smKey]);
        model[bigKey] = '';
        model[smKey] = '';
        imageObjs[bigKey] = ImageModel.empty();
        imageObjs[smKey] = ImageModel.empty();
        this.setState({ model, imageObjs });
    }

    componentDidMount() {
        var token = this.isProvider ? this.getProviderToken() : this.getAdminToken();
        if (token && token !== 'undefined') {
            if (this.props.match.params.id) {
                this.setState({ loading: true });
                let baseUrl = `${process.env.REACT_APP_API_URL}${this.isProvider ? 'provider/' : '/'}`;
                let url = `${baseUrl}get/${this.entity}/${this.props.match.params.id}`;
                fetch(url, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': token,
                        'Content-Type': 'application/json'
                    }
                })
                .then(res => {
                    if (res.status === 401) {
                        this.setState({ redirect: this.loginUrl})
                    } else {
                        res.json().then(res => {
                            if (res.data && res.data.length > 0) {
                                let model = res.data[0];
                                model.active = model.active === 1;
                                model.saleActive = model.saleActive === 1;
                                if (model.saleUntil) {
                                     model.saleUntil = model.saleUntil.substring(0, 10)
                                }
                                if (!Array.isArray(model.extraCategoryIds)) {
                                    model.extraCategoryIds = [];
                                }
                                if (Array.isArray(model.categoryIds)) {
                                    const primary = Number(model.categoryId);
                                    const sub = model.subCategoryId != null ? Number(model.subCategoryId) : null;
                                    model.extraCategoryIds = [];
                                    for (const raw of model.categoryIds) {
                                        const id = Number(raw);
                                        if (!Number.isFinite(id)) continue;
                                        if (id === primary || (sub != null && id === sub)) continue;
                                        model.extraCategoryIds.push(id);
                                    }
                                }
                                delete model.categoryIds;
                                let units = this.state.units;
                                if (['грамм', 'кг', 'мл', 'литр', 'ширхэг'].indexOf(model.unit) === -1) {
                                    // custom unit
                                       units.push({ key: model.unit, value: model.unit, text: model.unit })
                                }
                                // prepare image objs
                                this.state.imageObjs.lg = new ImageModel(undefined, model.imageLgUrl, true, true);
                                this.state.imageObjs.sm = new ImageModel(undefined, model.imageSmUrl, true, true);
                                this.state.imageObjs.xs = new ImageModel(undefined, model.imageXsUrl, true, true);
                                if (model.image1) {
                                    this.state.imageObjs.image1 = new ImageModel(undefined, model.image1, true, true);
                                }
                                if (model.image2) {
                                    this.state.imageObjs.image2 = new ImageModel(undefined, model.image2, true, true);
                                }
                                if (model.image3) {
                                    this.state.imageObjs.image3 = new ImageModel(undefined, model.image3, true, true);
                                }
                                if (model.image4) {
                                    this.state.imageObjs.image4 = new ImageModel(undefined, model.image4, true, true);
                                }
                                if (model.image5) {
                                    this.state.imageObjs.image5 = new ImageModel(undefined, model.image5, true, true);
                                }
                                if (model.image6) {
                                    this.state.imageObjs.image6 = new ImageModel(undefined, model.image6, true, true);
                                }
                                this.imageNamePrefix = String(model.name).toLowerCase().replace(/[^a-z0-9 ]/g, "").trim().replace(/\s+/g, "-")
                                console.log(model)
                                // Generate barcode image if barcode exists
                                let barcodeImage = '';
                                if (model.barCode && model.barCode.trim() !== '') {
                                    try {
                                        barcodeImage = createBarcode(model.barCode);
                                    } catch (error) {
                                        console.error('Error generating barcode:', error);
                                    }
                                }
                                
                                this.setState({
                                    model: model,
                                    units: units,
                                    loading: false,
                                    editing: true,
                                    imageObjs: this.state.imageObjs,
                                    barcodeImage: barcodeImage
                                });
                                this.originalModel = Object.assign({}, model);
                                this.originalModel.extraCategoryIds = [...(model.extraCategoryIds || [])];
                                this.fetchUsages(model.subCategoryId || model.categoryId);
                            }
                            this.getCategories();
                        })
                    }
                })
                .catch(err => console.error(err))
                .finally(() => {
                    this.setState({ loading: false });
                });

            } else {
                this.setState({ loading: false });
                this.getCategories();
            }

            // Get dropdown values
            this.getBrands();
        }
        else {
            this.setState({ redirect: this.loginUrl });
        }
    }

    getCategories() {
        var token = localStorage.getItem('providerToken')
        if (!token || token === 'undefined') {
            this.setState({ redirect: this.loginUrl })
            return;
        }

        fetch(process.env.REACT_APP_API_URL + "provider/list/category", {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': token,
                'Content-Type': 'application/json'
            }
        })
        .then(res => {
            if (res.status === 401) {
                this.setState({ redirect: this.loginUrl })
            }
            return res
        })
        .then(res => res.json())
        .then(res => {
            if (res.data) {
                let parentCategories: any[] = [];
                let subCategories: any[] = [];
                res.data.forEach((c: any) => {
                    if (!c.active) {
                        return;
                    }
                    if (!c.parentId) {
                        parentCategories.push({
                            key: c.id,
                            value: c.id,
                            text: c.name
                        });
                    } else {
                        subCategories.push({
                            key: c.id,
                            value: c.id,
                            text: c.name
                        });
                        this.allSubCategories.push(c);
                    }
                });
                this.allCategoryMultiOptions = res.data
                    .filter((c: any) => c.active)
                    .map((c: any) => ({ key: c.id, value: c.id, text: c.name }));
                this.setState({ parentCategories: parentCategories });
                if (this.state.model.categoryId) {
                    this.setState({
                        subCategories: this.allSubCategories
                            .filter(c => c.parentId === this.state.model.categoryId)
                            .map(c => new Object({ 'key': c.id, 'value': c.id, 'text': c.name }))
                    });
                }
            }
        })
        .catch(err => console.error(err));
    }

    getBrands() {
        var token = localStorage.getItem('providerToken')
        if (!token || token === 'undefined') {
            this.setState({ redirect: this.loginUrl })
            return;
        }

        fetch(process.env.REACT_APP_API_URL + "provider/list/brand", {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': token,
                'Content-Type': 'application/json'
            }
        })
        .then(res => {
            if (res.status === 401) {
                this.setState({ redirect: this.loginUrl })
            }
            return res
        })
        .then(res => res.json())
        .then(res => {
            console.log(res);
            if (res.data) {
                let data: any[] = [];
                res.data.forEach((c: any) => {
                    if (c.active) {
                        data.push({
                            key: c.id,
                            value: c.id,
                            text: c.name
                        });
                    }
                });
                this.setState({ brands: data });
            }
        })
        .catch(err => console.error(err));
    }

    onSaleActiveChange(e?: any) {
        let model: any = this.state.model;
        model.saleActive = Boolean(e.target.checked);
        this.setState({ model: model });
    }

    onSalePriceChange(e?: any) {
        let model: any = this.state.model;
        model.salePrice = Number(e.target.value);
        this.setState({ model: model });
    }

    onSaleUntilChange(e?: any) {
        let model: any = this.state.model;
        model.saleUntil = e.target.value;
        this.setState({ model: model });
    }

    onDeliveryDayChange(e?: any) {
        let model: any = this.state.model;
        model.deliveryDayMargin = e.target.value;
        this.setState({ model: model });
    }

    fetchUsages(categoryId: any) {
        if (this.triedUsages > 3) {
            return;
        }
        var token = localStorage.getItem('providerToken')
        if (!token || token === 'undefined') {
            this.setState({ redirect: this.loginUrl })
            return;
        }
        if (this.props.match.params.id) {
            this.triedUsages++;
            fetch(process.env.REACT_APP_API_URL + `provider/product/usage/${this.props.match.params.id}/${categoryId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token,
                    'Content-Type': 'application/json'
                }
            })
            .then(res => {
                if (res.status === 401) {
                    this.setState({ redirect: this.loginUrl })
                }
                return res
            })
            .then(res => res.json())
            .then(res => {
                if (res.data) {
                    let model: any = this.state.model;
                    let types: any[] = [];
                    let usages: any[] = [];
                    res.data.forEach((u: any) => {
                        u.active = (u.id !== null && u.id !== undefined)
                        u.productId = this.props.match.params.id
                        if (u.type === 'type') {
                            types.push(u);
                        } else if (u.type === 'usage') {
                            usages.push(u);
                        }
                    });
                    this.originalModel.usages = Object.assign([], usages.map(e => Object.assign({}, e)))
                    this.originalModel.types = Object.assign([], types.map(e => Object.assign({}, e)))
                    model.types = types;
                    model.usages = usages;
                    this.setState(model);
                }
            })
            .catch(err => {
                console.error(err)
                this.fetchUsages(categoryId);
            })
            .finally(() => {
                this.setState({ loading: false });
            });

        } else if (this.state.model.categoryId) {
            fetch(process.env.REACT_APP_API_URL + `provider/product/${categoryId}/usage`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token,
                    'Content-Type': 'application/json'
                }
            })
            .then(res => {
                if (res.status === 401) {
                    this.setState({ redirect: this.loginUrl })
                }
                return res
            })
            .then(res => res.json())
            .then(res => {
                if (res.data && res.data.length > 0) {
                    let model: any = this.state.model;
                    let types: any[] = [];
                    let usages: any[] = [];
                    res.data.forEach((u: any) => {
                        u.active = (u.id !== null && u.id !== undefined)
                        if (u.type === 'type') {
                            types.push(u);
                        } else if (u.type === 'usage') {
                            usages.push(u);
                        }
                    });
                    this.originalModel.usages = Object.assign([], usages)
                    this.originalModel.types = Object.assign([], types)
                    model.types = types;
                    model.usages = usages;
                    this.setState(model);
                }
                else {
                    let model: any = this.state.model;
                    model.types = [];
                    model.usages = [];
                    this.setState(model)
                }
            })
            .catch(err => console.error(err))
            .finally(() => {
                this.setState({ loading: false });
            });
        }
    }

    toggleUsageStatus(index: number, type: string) {
        let model: any = this.state.model;
        if (type === 'type') {
            model.types[index].active = !model.types[index].active
        } else if (type === 'usage') {
            model.usages[index].active = !model.usages[index].active
        }
        this.setState({ model: model })
    }

    onParentCategoryChange(e: any, drop: any) {
        let model: any = this.state.model;
        model.categoryId = drop.value;
        model.subCategoryId = null;
        model.extraCategoryIds = [];
        this.setState({
            model: model, subCategories: this.allSubCategories
                .filter(c => c.parentId === drop.value)
                .map(c => new Object({ key: c.id, value: c.id, text: c.name }))
        }, () => {
            this.triedUsages = 0;
            this.fetchUsages(model.categoryId);
        });
    }

    onSubCategoryChange(e: any, drop: any) {
        let model: any = this.state.model;
        model.subCategoryId = drop.value;
        this.setState({ model: model }, () => {
            this.triedUsages = 0;
            this.fetchUsages(model.subCategoryId);
        });
    }

    onExtraCategoriesChange = (e: any, drop: any) => {
        let model: any = this.state.model;
        model.extraCategoryIds = drop.value || [];
        this.setState({ model: model });
    }

    onBrandChange(e: any, drop: any) {
        let model: any = this.state.model;
        model.brandId = drop.value;
        this.setState({ model: model });
    }

    onSizeUnitChange(e: any, drop: any) {
        let model: any = this.state.model;
        model.unit = drop.value;
        this.setState({ model: model });
    }

    onUnitAdded(e:any, drop: any) {
        let units = this.state.units;
        units.push({ key: drop.value, value: drop.value, text: drop.value })
        this.setState({ units: units})
    }

    onBarcodeChange(e: any) {
        let model: any = this.state.model;
        model.barCode = e.target.value ? e.target.value : null;
        this.setState({ model: model });
        
        // Generate barcode image if barcode is not empty
        if (model.barCode && model.barCode.trim() !== '') {
            try {
                const barcodeImage = createBarcode(model.barCode);
                this.setState({ barcodeImage: barcodeImage });
            } catch (error) {
                console.error('Error generating barcode:', error);
                this.setState({ barcodeImage: '' });
            }
        } else {
            this.setState({ barcodeImage: '' });
        }
    }

    generateBarcode() {
        const newCode = generateUniqueCode();
        let model: any = this.state.model;
        model.barCode = newCode;
        this.setState({ model: model });
        
        try {
            const barcodeImage = createBarcode(newCode);
            this.setState({ barcodeImage: barcodeImage });
        } catch (error) {
            console.error('Error generating barcode:', error);
            this.setState({ barcodeImage: '' });
        }
    }

    printBarcodes() {
        if (!this.isBarcodesSaved()) {
            alert('Эхлээд баркодыг хадгална уу');
            return;
        }
        
        if (!this.state.barcodeImage) {
            alert('Please generate a barcode first');
            return;
        }

        // Navigate to barcode page with data
        this.props.history.push('/provider/barcode', {
            barcodeValue: this.state.model.barCode
        });
    }

    printPriceSigns() {
        if (!this.isBarcodesSaved()) {
            alert('Эхлээд баркодыг хадгална уу');
            return;
        }

        if (!this.state.barcodeImage) {
            alert('Please generate a barcode first');
            return;
        }

        this.props.history.push('/provider/price-sign', {
            barcodeValue: this.state.model.barCode,
            productName: this.state.model.name,
            productInfo: this.state.model.info,
            productSize: this.state.model.size,
            productUnit: this.state.model.unit,
            imageSmUrl: this.state.model.imageSmUrl,
            price: this.state.model.price,
            salePrice: this.state.model.saleActive ? this.state.model.salePrice : null,
            clubPrice: (this.state.model as any).clubPrice ?? null
        });
    }

    isBarcodesSaved(): boolean {
        // Check if the current barcode has been saved to the database
        return this.originalModel.barCode && 
               this.state.model.barCode === this.originalModel.barCode;
    }

    validate(): boolean {
        // prepare type string from typeArr
        // this.state.model.types = this.typeArr.join(",");

        this.setState({ successMsg: "" });
        if (!this.state.model.name) {
            this.setState({ errorMsg: "Бүтээгдэхүүний нэр оруулна уу" });
            return false;
        }
        if (this.state.editingSlug || !this.state.slugValid || !this.state.model.slug) {
            this.setState({ errorMsg: "URL нэрийг шалгана уу." });
            return false;
        }
        if (!this.state.model.categoryId) {
            this.setState({ errorMsg: "Үндсэн категори сонгоно уу" });
            return false;
        }
        if (this.state.subCategories.length > 0 && !this.state.model.subCategoryId) {
            this.setState({ errorMsg: "Дэд категори сонгоно уу" });
            return false;
        }
        if (!this.state.model.brandId) {
            this.setState({ errorMsg: "Брэнд сонгоно уу" });
            return false;
        }
        if (!this.state.model.price) {
            this.setState({ errorMsg: "Бүтээгдэхүүний үнэ оруулна уу" });
            return false;
        }
        if (this.state.model.saleActive) {
            if (!this.state.model.salePrice) {
                this.setState({ errorMsg: "Хямдарсан үнэ оруулна уу" });
                return false;
            }
            if (this.state.model.salePrice >= this.state.model.price) {
                this.setState({ errorMsg: "Хямдарсан үнэ үндсэн үнээс бага байх хэрэгтэй" });
                return false;
            }
            if (!this.state.model.saleUntil) {
                this.setState({ errorMsg: "Хямдралын сүүлийн өдрийг оруулна уу" });
                return false;
            }
        }
        if (!this.state.model.deliveryDayMargin) {
            this.setState({ errorMsg: "Хүргэх өдрийн интервал оруулна уу" });
            return false;
        }
        if (this.state.model.deliveryDayMargin < 1 || this.state.model.deliveryDayMargin > 50) {
            this.setState({ errorMsg: "Хүргэх өдрийн интервал 1-50 байх ёстой" });
            return false;
        }
        //TODO validate barcode later
        if (!this.state.imageObjs.lg.file && !this.state.model.imageLgUrl) {
            this.setState({ errorMsg: "Том хэмжээтэй зураг оруулна уу" });
            return false;
        }
        if (!this.state.imageObjs.sm.file && !this.state.model.imageSmUrl) {
            this.setState({ errorMsg: "Дунд хэмжээтэй зураг оруулна уу" });
            return false;
        }
        if (!this.state.imageObjs.xs.file && !this.state.model.imageXsUrl) {
            this.setState({ errorMsg: "Жижиг хэмжээтэй зураг оруулна уу" });
            return false;
        }
        if (this.state.model.id !== undefined) {
            const sortNum = (a: any, b: any) => Number(a) - Number(b);
            const curEx = [...(this.state.model.extraCategoryIds || [])].sort(sortNum).join(',');
            const origEx = [...(this.originalModel.extraCategoryIds || [])].sort(sortNum).join(',');
            if (this.state.model.name === this.originalModel.name
                && this.state.model.shortName === this.originalModel.shortName
                && this.state.model.slug === this.originalModel.slug
                && this.state.model.categoryId === this.originalModel.categoryId
                && this.state.model.subCategoryId === this.originalModel.subCategoryId
                && curEx === origEx
                && this.state.model.brandId === this.originalModel.brandId
                && this.state.model.providerId === this.originalModel.providerId
                && this.state.model.info === this.originalModel.info
                && this.state.model.size === this.originalModel.size
                && this.state.model.unit === this.originalModel.unit
                && this.state.model.price === this.originalModel.price
                && this.state.model.saleActive === this.originalModel.saleActive
                && this.state.model.salePrice === this.originalModel.salePrice
                && this.state.model.saleUntil === this.originalModel.saleUntil
                && this.state.model.deliveryDayMargin === this.originalModel.deliveryDayMargin
                && this.state.model.barCode === this.originalModel.barCode
                && this.state.imageObjs.lg.name === this.originalModel.imageLgUrl
                && this.state.imageObjs.sm.name === this.originalModel.imageSmUrl
                && this.state.imageObjs.xs.name === this.originalModel.imageXsUrl
                && this.state.imageObjs.image1.name === this.originalModel.image1
                && this.state.imageObjs.image2.name === this.originalModel.image2
                && this.state.imageObjs.image3.name === this.originalModel.image3
                && this.state.imageObjs.image4.name === this.originalModel.image4
                && this.state.imageObjs.image5.name === this.originalModel.image5
                && this.state.imageObjs.image6.name === this.originalModel.image6
                && this.state.model.active === this.originalModel.active
                && this.state.model.usages?.filter(u => u.active).map(u => u.name).join(",") === this.originalModel.usages?.filter((u: any) => u.active).map((u: any) => u.name).join(",")
                && this.state.model.types?.filter(u => u.active).map(u => u.name).join(",") === this.originalModel.types?.filter((u: any) => u.active).map((u: any) => u.name).join(",")
            ) {
                this.setState({ errorMsg: "Өөрчлөлт байхгүй байна" });
                return false;
            }
        }
        this.setState({ errorMsg: "" });
        return true;
    }

    render() {
        if (this.state.redirect) {
            return <Redirect to={this.state.redirect} />
        }
        return (
            <Container className="page-admin">
                <Dimmer active={this.state.loading} inverted>
                    <Loader size='medium'></Loader>
                </Dimmer>
                <h2>{!this.state.editing ? 'Шинэ' : ''} Бүтээгдэхүүн</h2>
                <Form>
                    {!this.state.editing ?
                        <Form.Group className="flex-start">
                            <Icon name="info circle" color="orange" />
                            <p>Шинэ бүтээгдэхүүн үүсгэхдээ тухайн бүтээгдэхүүнд харгалзах категори, брэнд, нийлүүлэгчийг эхлээд үүсгэнэ үү</p>
                        </Form.Group>
                        : null}
                    <Form.Group>
                        {this.state.successMsg ? <div className="success-msg">{this.state.successMsg}</div> : null}
                        {this.state.errorMsg ? <div className="error-msg">
                            <Icon name="warning circle" />
                            {this.state.errorMsg}
                        </div> : null}
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <Form.Input required label='Бүтээгдэхүүний нэр' value={this.state.model.name}
                            onBlur={this.onNameBlur}
                            onChange={this.onNameChange} />
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <Form.Field label='Бүтээгдэхүүний товч нэр' control='input' value={this.state.model.shortName} onChange={this.onShortNameChange} />
                    </Form.Group>
                    <Form.Group>
                        <div className="required field">
                            <label className="required">URL нэр</label>
                            { this.state.editingSlug ?
                            <React.Fragment>
                            <div className="flex-start">
                                <Form.Input required value={this.state.model.slug || ""}
                                    onBlur={this.onSlugBlur}
                                    onChange={this.onSlugChange} />
                                { this.state.slugValid ? null :
                                    <React.Fragment>
                                        <Icon name="warning circle" color='red' size='large'/>
                                        <span className="error-msg">URL нэр ашиглагдсан байна</span>
                                    </React.Fragment>
                                }
                                &nbsp;&nbsp;
                                <Button className="cursor" color='orange'
                                    disabled={this.originalModel.slug === this.state.model.slug}
                                    onClick={this.postNewSlug}>Шалгах</Button>
                                <Button className="cursor" onClick={this.cancelSlugChange}>Болих</Button>
                            </div>
                            <div>
                                <br/>
                                <Icon name="info circle" color="blue" />
                                Зөвхөн англи үсэг болон - тэмдэг ашиглана
                            </div>
                            </React.Fragment>
                            :
                            <div>
                                <Label>{`${process.env.REACT_APP_DOMAIN}/product/${this.state.model.slug}`}</Label>
                                { this.state.slugValid ?
                                    (this.state.model.slug && this.state.model.slug.length > 0) ?
                                        <Icon name="check circle" color='green' size='large'/> : null :
                                    <Icon name="warning circle" color='red' size='large'/>
                                }
                                &nbsp;&nbsp;
                                <Icon className="cursor" name="edit outline" size='large' onClick={this.toggleSlug}/>
                            </div>
                        }
                        </div>
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <div className="field">
                            <label>Категори</label>
                        </div>
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <Form.Dropdown placeholder='Үндсэн категори' fluid labeled clearable selection options={this.state.parentCategories}
                            value={this.state.model.categoryId}
                            onChange={this.onParentCategoryChange} />
                    </Form.Group>
                    <Form.Group widths='equal'>
                        {this.state.subCategories.length > 0 ?
                            <Form.Dropdown placeholder='Дэд категори' fluid labeled clearable selection options={this.state.subCategories}
                                value={this.state.model.subCategoryId}
                                onChange={this.onSubCategoryChange} /> : <p>Дэд категорийн сонголт хоосон байна</p>}
                    </Form.Group>
                    {this.state.editing && (this.originalModel.categoryId !== this.state.model.categoryId || this.originalModel.subCategoryId !== this.state.model.subCategoryId || [...(this.originalModel.extraCategoryIds || [])].sort((a: any, b: any) => Number(a) - Number(b)).join(',') !== [...(this.state.model.extraCategoryIds || [])].sort((a: any, b: any) => Number(a) - Number(b)).join(',')) ?
                        <Form.Group>
                            <p>Категори солигдоход өмнө нь байсан төрлүүд болон хэрэглээнүүд байхгүй болохыг анхаарна уу</p>
                        </Form.Group> : null}
                    <Form.Group widths='equal'>
                        <Form.Dropdown placeholder='Нэмэлт категориуд' fluid multiple search selection clearable
                            options={this.allCategoryMultiOptions}
                            value={this.state.model.extraCategoryIds || []}
                            onChange={this.onExtraCategoriesChange} />
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <div className="field">
                            <label>Брэнд</label>
                        </div>
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <Form.Dropdown placeholder='Брэнд' fluid labeled clearable selection search options={this.state.brands}
                            value={this.state.model.brandId}
                            onChange={this.onBrandChange} />
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <div className="field">
                            <label>Хэмжээ</label>
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Input value={this.state.model.size} onChange={this.onSizeChange} />
                        <Form.Dropdown placeholder='Хэмжээс' fluid labeled clearable selection options={this.state.units}
                            search allowAdditions
                            value={this.state.model.unit}
                            onAddItem={this.onUnitAdded}
                            onChange={this.onSizeUnitChange} />
                    </Form.Group>
                    <Form.Group widths='equal'>
                        <Form.Input
                            type="number"
                            required
                            label='Бүтээгдэхүүний үнэ (₮)'
                            value={this.state.model.price ?? ''}
                            onChange={this.onPriceChange}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Checkbox checked={this.state.model.saleActive} onChange={this.onSaleActiveChange} label='Хямдарсан' />
                    </Form.Group>
                    { this.state.model.saleActive ? <React.Fragment>
                        <hr/>
                        <Form.Group widths='equal'>
                            <Form.Input
                                required
                                label='Хямдарсан үнэ (₮)'
                                value={this.state.model.salePrice ?? ''}
                                onChange={this.onSalePriceChange}
                            />
                        </Form.Group>
                        <Form.Group widths='equal'>
                            <Form.Input type='date' required label='Хямдралын сүүлийн өдөр' value={this.state.model.saleUntil} onChange={this.onSaleUntilChange} />
                        </Form.Group>
                        <hr/>
                    </React.Fragment> : null }

                    <Form.Group widths='equal'>
                        <Form.Input
                            type="number"
                            required
                            label='Хүргэх өдрийн интервал (хэдэн өдрийн дараа хүргэх боломжтой)'
                            min={0}
                            max={50}
                            value={this.state.model.deliveryDayMargin ?? ''}
                            onChange={this.onDeliveryDayChange}
                        />
                    </Form.Group>
                    
                    <Form.Group>
                        <Form.TextArea label='Тайлбар' width="16" value={this.state.model.info} onChange={this.onInfoChange} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Checkbox checked={this.state.model.active} onChange={this.onActiveChange} label='Идэвхитэй' />
                    </Form.Group>

                    <hr />
                    <br/>

                    <Form.Group>
                        <Form.Input label='Баркод' width="16" placeholder='Баркод код оруулах' value={this.state.model.barCode || ''} onChange={this.onBarcodeChange} onKeyDown={(e: any) => { if (e.key === 'Enter') e.preventDefault(); }} />
                    </Form.Group>

                    <Form.Group>
                        <Form.Field width={16}>
                            <Button fluid primary type="button" onClick={this.generateBarcode}>
                                <Icon name="qrcode" />
                                Шинэ баркод үүсгэх
                            </Button>
                        </Form.Field>
                    </Form.Group>

                    {this.state.barcodeImage && (
                        <>
                        <Form.Group>
                            <Form.Field>
                            <img 
                                src={this.state.barcodeImage} 
                                alt="Barcode" 
                                style={{ maxWidth: '300px', height: 'auto' }}
                            />
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={16}>
                                <Button 
                                    fluid 
                                    type="button" 
                                    onClick={() => this.printBarcodes()}
                                    disabled={!this.isBarcodesSaved()}
                                    title={!this.isBarcodesSaved() ? 'Эхлээд баркодыг хадгална уу' : ''}
                                >
                                    Print
                                    {!this.isBarcodesSaved() && <span style={{ marginLeft: '8px' }}>(Хадгалах шаардлагатай)</span>}
                                </Button>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group>
                            <Form.Field width={16}>
                                <Button
                                    fluid
                                    type="button"
                                    onClick={() => this.printPriceSigns()}
                                    disabled={!this.isBarcodesSaved()}
                                    title={!this.isBarcodesSaved() ? 'Эхлээд баркодыг хадгална уу' : ''}
                                >
                                    Print A4 Price Signs
                                </Button>
                            </Form.Field>
                        </Form.Group>
                        </>
                    )}
                    
                    <hr />
                    <br/>
                    
                    <Form.Group>
                        <Label color="teal">Нүүр зураг</Label>
                    </Form.Group>
                    <Form.Group className="flex-space-between">
                        {/* <div className="field">
                            <label>Зураг (800×800 тайрна)</label>
                        </div> */}
                        {this.state.model.imageLgUrl ? (
                            <ImageOnS3 imgKey={this.state.model.imageLgUrl} width='400px' height='300px' />
                        ) : null}
                        <Form.Input control='input' type="file" accept="image/png, image/jpeg, image/webp" onChange={this.onMainImageSelected} />
                    </Form.Group>
                    <hr />
                    <Form.Group>
                        <Label color="orange">Бүтээгдэхүүний зураг 1</Label>
                    </Form.Group>
                    <Form.Group className="flex-space-between">
                        {/* <div className="field">
                            <label>Зураг (800×800 тайрна)</label>
                        </div> */}
                        {this.state.model.image1 ? (
                            <div className="flex-space-between">
                                <ImageOnS3 imgKey={this.state.model.image1} width="400px" height="300px" />
                                <Icon className="cursor margin-10" name="trash" size="large" onClick={() => this.deleteProductImage(1)} />
                            </div>
                        ) : null}
                        <Form.Input control="input" type="file" accept="image/png, image/jpeg, image/webp" onChange={this.onProductImageSelected(1)} />
                    </Form.Group>
                    <hr />
                    <Form.Group>
                        <Label color="yellow">Бүтээгдэхүүний зураг 2</Label>
                    </Form.Group>
                    <Form.Group className="flex-space-between">
                        {/* <div className="field">
                            <label>Зураг (800×800 тайрна)</label>
                        </div> */}
                        {this.state.model.image2 ? (
                            <div className="flex-space-between">
                                <ImageOnS3 imgKey={this.state.model.image2} width="400px" height="300px" />
                                <Icon className="cursor margin-10" name="trash" size="large" onClick={() => this.deleteProductImage(2)} />
                            </div>
                        ) : null}
                        <Form.Input control="input" type="file" accept="image/png, image/jpeg, image/webp" onChange={this.onProductImageSelected(2)} />
                    </Form.Group>
                    <hr />
                    <Form.Group>
                        <Label color="olive">Бүтээгдэхүүний зураг 3</Label>
                    </Form.Group>
                    <Form.Group className="flex-space-between">
                        {/* <div className="field">
                            <label>Зураг (800×800 тайрна)</label>
                        </div> */}
                        {this.state.model.image3 ? (
                            <div className="flex-space-between">
                                <ImageOnS3 imgKey={this.state.model.image3} width="400px" height="300px" />
                                <Icon className="cursor margin-10" name="trash" size="large" onClick={() => this.deleteProductImage(3)} />
                            </div>
                        ) : null}
                        <Form.Input control="input" type="file" accept="image/png, image/jpeg, image/webp" onChange={this.onProductImageSelected(3)} />
                    </Form.Group>
                    <hr />
                    {this.state.model.types?.length > 0 || this.state.model.usages?.length > 0 ? <React.Fragment>
                        <Form.Group>
                            <div className="field">
                                <label>Бүтээгдэхүүний төрлүүд</label>
                            </div>
                        </Form.Group>
                        <Form.Group>
                            <div className="category-types">
                                {this.state.model.types?.map((type: any, index: number) => {
                                    return <Label
                                        key={"category-type-" + index}
                                        className="cursor"
                                        color={type.active ? 'blue' : 'grey'}
                                        onClick={() => this.toggleUsageStatus(index, 'type')}>{type.name}&nbsp;&nbsp;</Label>
                                })}
                            </div>
                        </Form.Group>
                        <Form.Group>
                            <div className="field">
                                <label>Бүтээгдэхүүний хэрэглээ</label>
                            </div>
                        </Form.Group>
                        <Form.Group>
                            <div className="category-usages">
                                {this.state.model.usages?.map((usage: any, index: number) => {
                                    return <Label
                                        className="cursor"
                                        color={usage.active ? 'blue' : 'grey'}
                                        key={usage.usageId != null ? `usage-${usage.usageId}` : `usage-${index}`}
                                        onClick={() => this.toggleUsageStatus(index, 'usage')}
                                    >{usage.name}&nbsp;&nbsp;</Label>
                                })}
                            </div>
                        </Form.Group>
                        <hr />
                    </React.Fragment> : null}
                    <br />
                    <Form.Button fluid disabled={this.state.loading} onClick={() => this.save()}>Хадгалах</Form.Button>
                </Form>

                <ImageCropperModal
                    open={this.state.cropOpen}
                    imageSrc={this.state.cropImageSrc}
                    onClose={() => this.setState({ cropOpen: false, cropForSlot: null, cropSourceFileSize: null })}
                    onCropped={this.handleCropDone}
                    outputSize={800}
                    sourceFileSizeBytes={this.state.cropSourceFileSize ?? undefined}
                />
            </Container>
        );
    }
}

export default ProviderProductForm;